import { useEffect, useRef, useState } from "react";
import AppShell from "@/components/AppShell";
import { api, inr, formatErr } from "@/lib/api";
import AllergenBadge from "@/components/AllergenBadge";
import { toast } from "sonner";
import { Plus, Edit, Trash2, X, BadgeIndianRupee, Upload, Sparkles, Loader2, Download, Zap } from "lucide-react";

const TAGS = ["veg", "vegan", "gf", "nuts", "dairy", "spicy", "egg"];

const empty = {
  name: "", category: "Snacks", price: 50, cost_price: 0,
  description: "", image_url: "", dietary_tags: [], allergens: [],
  available: true, stock_today: 100, is_junk_food: false,
  tax_rate: 5.0, is_mrp: false, hsn_code: "", nutrition: null,
};

export default function AdminMenu() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [genBusy, setGenBusy] = useState(false);
  const [nutBusy, setNutBusy] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [autoImg, setAutoImg] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const fileRef = useRef(null);

  const load = () => api.get("/menu").then(r => setItems(r.data));
  useEffect(() => {
    load();
    api.get("/vendors").then(r => setVendors(r.data)).catch(()=>setVendors([]));
  }, []);

  const save = async () => {
    try {
      const payload = {
        ...editing,
        price: Number(editing.price),
        cost_price: Number(editing.cost_price || 0),
        stock_today: Number(editing.stock_today || 0),
        tax_rate: Number(editing.tax_rate || 0),
      };
      if (editing.id) await api.put(`/menu/${editing.id}`, payload);
      else await api.post("/menu", payload);
      toast.success(editing.id ? "Updated" : "Created");
      setEditing(null); load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const generateImage = async () => {
    if (!editing?.name) return toast.error("Add a name first");
    setGenBusy(true);
    try {
      const tags = (editing.dietary_tags || []).join(", ");
      const prompt = `${editing.name} (${editing.category}${tags ? ", " + tags : ""}${editing.description ? " — " + editing.description : ""})`;
      const { data } = await api.post("/menu-bulk/generate-image", { prompt });
      setEditing({ ...editing, image_url: data.image_url });
      toast.success("AI image generated");
    } catch (e) { toast.error(formatErr(e)); }
    finally { setGenBusy(false); }
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBulkBusy(true);
    setBulkResult(null);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post(`/menu-bulk/upload?auto_image=${autoImg ? "true" : "false"}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setBulkResult(data);
      toast.success(`Created ${data.created_count} items${data.skipped ? ` (skipped ${data.skipped})` : ""}`);
      load();
    } catch (err) { toast.error(formatErr(err)); }
    finally {
      setBulkBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    try { await api.delete(`/menu/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const toggleSoldOut = async (it) => {
    try { await api.patch(`/menu/${it.id}/sold-out?sold_out=${!it.sold_out}`); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const margin = (i) => {
    const cp = i.cost_price || 0;
    if (!cp || !i.price) return null;
    return Math.round(((i.price - cp) / i.price) * 100);
  };

  return (
    <AppShell variant="admin">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-brand">Menu</div>
            <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-ink mt-2">Today's offering</h1>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button data-testid="bulk-template" onClick={async () => {
              try {
                const { data } = await api.get("/menu-bulk/template.csv", { responseType: "blob" });
                const url = URL.createObjectURL(data);
                const a = document.createElement("a"); a.href = url; a.download = "grubpe-menu-template.csv"; a.click();
                URL.revokeObjectURL(url);
              } catch (err) { toast.error(formatErr(err)); }
            }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cream-200 text-ink-700 text-xs font-bold hover:bg-cream-300">
              <Download size={14} /> CSV template
            </button>
            <label className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cream-200 cursor-pointer">
              <input data-testid="bulk-auto-image" type="checkbox" checked={autoImg} onChange={(e)=>setAutoImg(e.target.checked)} className="accent-brand"/>
              <span className="text-xs font-bold inline-flex items-center gap-1"><Sparkles size={12}/> AI images</span>
            </label>
            <button data-testid="bulk-upload-btn" onClick={()=>fileRef.current?.click()} disabled={bulkBusy}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-ink text-white text-xs font-bold disabled:opacity-60">
              {bulkBusy ? <Loader2 size={14} className="animate-spin"/> : <Upload size={14}/>} {bulkBusy ? "Uploading…" : "Bulk upload (CSV/XLSX)"}
            </button>
            <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" onChange={onFile} className="hidden" data-testid="bulk-file"/>
            <button data-testid="new-item-btn" onClick={() => setEditing({ ...empty })}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-brand text-white font-semibold hover:bg-brand-600 active:scale-95">
              <Plus size={16} /> New item
            </button>
          </div>
        </div>

        {bulkResult && (
          <div data-testid="bulk-result" className="mb-5 p-4 rounded-2xl bg-emerald-50 border border-emerald-200">
            <div className="text-sm font-bold text-emerald-900">Bulk import: {bulkResult.created_count} created · {bulkResult.skipped} skipped</div>
            {bulkResult.errors?.length > 0 && (
              <div className="mt-2 text-xs text-amber-900 space-y-0.5">
                {bulkResult.errors.slice(0, 8).map((er, i) => <div key={i}>Row {er.row}: {er.reason}</div>)}
                {bulkResult.errors.length > 8 && <div>…and {bulkResult.errors.length - 8} more</div>}
              </div>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {items.map(it => {
            const m = margin(it);
            return (
              <div key={it.id} className="bg-white rounded-xl border border-border overflow-hidden">
                <div className="aspect-[4/3] bg-cream-200 overflow-hidden relative">
                  {it.image_url && <img src={it.image_url} alt={it.name} className="w-full h-full object-cover" />}
                  {it.sold_out && <div className="absolute inset-0 bg-ink/60 flex items-center justify-center"><span className="text-white font-bold">Sold out</span></div>}
                  {it.is_mrp && <span className="absolute top-2 left-2 text-[9px] font-bold uppercase px-2 py-0.5 rounded-full bg-sun text-ink shadow-sm">MRP</span>}
                </div>
                <div className="p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-heading font-bold text-ink truncate">{it.name}</div>
                      <div className="text-[11px] uppercase tracking-wider text-ink-500">{it.category} · GST {it.tax_rate}%</div>
                    </div>
                    <div className="text-right">
                      <div className="font-heading font-extrabold text-ink">{inr(it.price)}</div>
                      <div className="text-[10px] text-ink-500">cost {inr(it.cost_price || 0)}{m !== null ? ` · ${m}%` : ""}</div>
                    </div>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1">
                    {it.dietary_tags?.map(t => <AllergenBadge key={t} tag={t} />)}
                  </div>
                  <div className="mt-4 flex items-center gap-2">
                    <button data-testid={`edit-${it.id}`} onClick={() => setEditing(it)} className="p-2 rounded-lg bg-cream-200 hover:bg-cream-300 text-ink-700"><Edit size={14} /></button>
                    <button data-testid={`delete-${it.id}`} onClick={() => remove(it.id)} className="p-2 rounded-lg bg-red-50 hover:bg-red-100 text-brand"><Trash2 size={14} /></button>
                    <button data-testid={`soldout-${it.id}`} onClick={() => toggleSoldOut(it)} className="ml-auto px-3 py-1.5 rounded-lg text-xs font-semibold bg-cream-200 hover:bg-cream-300">{it.sold_out ? "Mark available" : "Mark sold out"}</button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {editing && (
        <div className="fixed inset-0 bg-ink/40 z-50 flex items-center justify-center px-4 py-8 overflow-y-auto" onClick={() => setEditing(null)}>
          <div className="bg-white rounded-3xl p-6 sm:p-8 w-full max-w-2xl shadow-floating" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <div>
                <div className="font-heading text-2xl font-bold text-ink">{editing.id ? "Edit item" : "New item"}</div>
                <div className="text-xs text-ink-500 mt-1 flex items-center gap-1"><BadgeIndianRupee size={12} /> Track cost + selling price + GST + MRP</div>
              </div>
              <button onClick={() => setEditing(null)} className="text-ink-500 hover:text-ink"><X size={20} /></button>
            </div>

            <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Name" value={editing.name} onChange={v => setEditing({...editing, name: v})} testid="form-name" />
              <Field label="Category" value={editing.category} onChange={v => setEditing({...editing, category: v})} testid="form-cat" />

              <Field label="Selling price ₹" type="number" value={editing.price} onChange={v => setEditing({...editing, price: v})} testid="form-price" />
              <Field label="Cost / purchase ₹" type="number" value={editing.cost_price} onChange={v => setEditing({...editing, cost_price: v})} testid="form-cost" hint={editing.price && editing.cost_price ? `Margin: ${Math.round(((Number(editing.price)-Number(editing.cost_price))/Number(editing.price))*100)}%` : ""} />

              <Field label="GST rate %" type="number" value={editing.tax_rate} onChange={v => setEditing({...editing, tax_rate: v})} testid="form-tax" hint="5% restaurant food, 12% packaged" />
              <Field label="HSN code" value={editing.hsn_code} onChange={v => setEditing({...editing, hsn_code: v})} testid="form-hsn" hint="Optional" />

              <label className="flex items-center gap-2 p-3 bg-cream-100 rounded-xl cursor-pointer">
                <input data-testid="form-mrp" type="checkbox" checked={!!editing.is_mrp} onChange={e => setEditing({...editing, is_mrp: e.target.checked})} />
                <span className="text-sm font-semibold">Price is MRP-inclusive (tax already in)</span>
              </label>
              <label className="flex items-center gap-2 p-3 bg-cream-100 rounded-xl cursor-pointer">
                <input data-testid="form-junk" type="checkbox" checked={!!editing.is_junk_food} onChange={e => setEditing({...editing, is_junk_food: e.target.checked})} />
                <span className="text-sm font-semibold">Counts as junk food (parental limit)</span>
              </label>

              <div className="sm:col-span-2">
                <label className="text-xs font-bold uppercase tracking-wider text-ink-500">Counter / Vendor</label>
                <select data-testid="form-vendor" value={editing.vendor_id ?? ""} onChange={e => setEditing({...editing, vendor_id: e.target.value || null})}
                  className="mt-1 w-full rounded-xl border border-border px-4 py-2.5 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20">
                  <option value="">— Unassigned —</option>
                  {vendors.map(v => <option key={v.id} value={v.id}>{v.name}{v.counter_label ? ` · ${v.counter_label}` : ""}{v.operator_type === "school" ? " (school)" : ""}</option>)}
                </select>
              </div>

              <div className="sm:col-span-2">
                <label className="text-xs font-bold uppercase tracking-wider text-ink-500 flex items-center justify-between">
                  <span>Image</span>
                  <button type="button" data-testid="form-img-ai" onClick={generateImage} disabled={genBusy}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-ink text-white text-[11px] font-bold disabled:opacity-60">
                    {genBusy ? <Loader2 size={11} className="animate-spin"/> : <Sparkles size={11}/>}
                    {genBusy ? "Generating…" : "Generate with AI"}
                  </button>
                </label>
                {editing.image_url && (
                  <div className="mt-2 aspect-[4/3] w-full rounded-xl overflow-hidden bg-cream-200 border border-border">
                    <img src={editing.image_url} alt="preview" className="w-full h-full object-cover"/>
                  </div>
                )}
                <input data-testid="form-img" type="text" placeholder="Paste an image URL or click Generate with AI"
                  value={editing.image_url ?? ""} onChange={(e)=>setEditing({...editing, image_url: e.target.value})}
                  className="mt-2 w-full rounded-xl border border-border bg-white px-4 py-2.5 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20" />
              </div>

              <div className="sm:col-span-2">
                <label className="text-xs font-bold uppercase tracking-wider text-ink-500">Description</label>
                <textarea data-testid="form-desc" value={editing.description} onChange={e => setEditing({...editing, description: e.target.value})} rows={2}
                  className="mt-1 w-full rounded-xl border border-border bg-white px-4 py-2.5 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20" />
              </div>

              <div className="sm:col-span-2">
                <label className="text-xs font-bold uppercase tracking-wider text-ink-500">Dietary tags</label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {TAGS.map(t => {
                    const active = editing.dietary_tags?.includes(t);
                    return (
                      <button key={t} data-testid={`tag-toggle-${t}`}
                        onClick={() => setEditing({
                          ...editing,
                          dietary_tags: active ? editing.dietary_tags.filter(x => x !== t) : [...(editing.dietary_tags || []), t],
                        })}
                        className={`px-3 py-1 rounded-full text-xs font-bold uppercase border ${active ? "bg-ink text-white border-ink" : "bg-white text-ink-700 border-border"}`}>
                        {t}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Nutrition per serving */}
              <div className="sm:col-span-2 border-t border-border pt-4 mt-2">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-bold uppercase tracking-wider text-ink-500">Nutrition (per serving)</span>
                  <button type="button" data-testid="menu-ai-nutrition-btn" onClick={async () => {
                    if (!editing?.name) return toast.error("Add a name first");
                    setNutBusy(true);
                    try {
                      const { data } = await api.post("/ai/nutrition/suggest", { name: editing.name, description: editing.description || "" });
                      setEditing(prev => ({ ...prev, nutrition: data }));
                      toast.success("Nutrition values suggested by AI");
                    } catch (e) { toast.error("AI suggestion failed"); }
                    finally { setNutBusy(false); }
                  }} disabled={nutBusy}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-brand-50 text-brand-700 text-[11px] font-semibold hover:bg-brand-100 disabled:opacity-60">
                    {nutBusy ? <Loader2 size={12} className="animate-spin"/> : <Zap size={12}/>}
                    {nutBusy ? "Suggesting…" : "AI Suggest"}
                  </button>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {[["calories","Calories (kcal)"],["protein","Protein (g)"],["carbs","Carbs (g)"],["fat","Fat (g)"],["fiber","Fiber (g)"],["serving_size","Serving size"]].map(([k,label]) => (
                    <div key={k}>
                      <label className="text-[10px] text-ink-500">{label}</label>
                      <input data-testid={`menu-nutrition-${k}`} value={editing.nutrition?.[k] ?? ""} onChange={e => {
                        const val = k === "serving_size" ? e.target.value : (e.target.value === "" ? "" : Number(e.target.value));
                        setEditing(prev => ({ ...prev, nutrition: { ...(prev.nutrition || {}), [k]: val } }));
                      }} className="w-full rounded-lg border border-border px-2 py-1.5 text-sm focus:border-brand focus:outline-none" />
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-2">
              <button onClick={() => setEditing(null)} className="px-5 py-2.5 rounded-full text-ink-700 font-semibold hover:bg-cream-200">Cancel</button>
              <button data-testid="form-save" onClick={save} className="px-5 py-2.5 rounded-full bg-brand text-white font-semibold hover:bg-brand-600 active:scale-95">Save</button>
            </div>
          </div>
        </div>
      )}
    </AppShell>
  );
}

const Field = ({ label, value, onChange, type = "text", testid, hint }) => (
  <div>
    <label className="text-xs font-bold uppercase tracking-wider text-ink-500">{label}</label>
    <input data-testid={testid} type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)}
      className="mt-1 w-full rounded-xl border border-border bg-white px-4 py-2.5 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20" />
    {hint && <div className="text-[11px] text-ink-500 mt-1">{hint}</div>}
  </div>
);
